import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Check, AlertTriangle, HelpCircle, ArrowRight, RotateCw, MessageSquare, Sparkles } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { AnalysisDecision, AnalysisPayload, AnalysisRecommendation } from '@/lib/workflowStreamProtocol';

interface RecordedDecision {
  question: string;
  chosen: string;
}

interface AnalysisArtifactCardProps {
  analysis: AnalysisPayload;
  nextStepLabel?: string;
  /** Decisions the user has already recorded for this step. */
  recordedDecisions?: RecordedDecision[];
  /** Called when the user picks an answer for a decision. */
  onRecordDecision?: (decision: RecordedDecision) => void;
  onProceed?: () => void;
  onIterate?: () => void;
}

// Effective recommendation: prefer the field if set, otherwise infer from
// what's actually in the analysis (back-compat with rows generated before
// the recommendation field existed).
function effectiveRecommendation(a: AnalysisPayload): AnalysisRecommendation {
  if (a.recommendation) return a.recommendation;
  if (a.sections.decisions.length > 0) return 'decide';
  if (!a.summary.parsedCleanly || a.sections.worthFlagging.some(b => b.severity === 'block')) return 'refine';
  return 'proceed';
}

const RECOMMENDATION_BANNER: Record<AnalysisRecommendation, { label: string; className: string }> = {
  proceed: {
    label: 'Cosmo recommends moving to the next step.',
    className: 'bg-success/10 border-success/30 text-success-foreground',
  },
  refine: {
    label: 'Cosmo recommends refining this step before moving on.',
    className: 'bg-warning/10 border-warning/30 text-warning-foreground',
  },
  decide: {
    label: 'Pick a direction below — the next prompt will incorporate it.',
    className: 'bg-info/10 border-info/30 text-info-foreground',
  },
};

export function AnalysisArtifactCard({
  analysis,
  nextStepLabel,
  recordedDecisions = [],
  onRecordDecision,
  onProceed,
  onIterate,
}: AnalysisArtifactCardProps) {
  const recommendation = effectiveRecommendation(analysis);
  const banner = RECOMMENDATION_BANNER[recommendation];
  return (
    <div className="rounded-xl border border-primary/30 bg-primary/5 overflow-hidden">
      <div className="px-4 py-3 border-b border-primary/20 flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 min-w-0">
          <Badge variant="outline" className="text-[10px] h-5 px-1.5 bg-success/15 border-success/30 text-success-foreground shrink-0">
            <Check className="h-2.5 w-2.5 mr-0.5" />
            reviewed
          </Badge>
          <p className="text-sm font-medium truncate">{analysis.summary.headline}</p>
        </div>
        {analysis.summary.rowsCount !== undefined && (
          <span className="text-[11px] text-muted-foreground shrink-0">{analysis.summary.rowsCount} rows</span>
        )}
      </div>

      <div className={cn(
        'flex items-center gap-2 px-4 py-2 text-xs border-b',
        banner.className,
      )}>
        {recommendation === 'proceed' ? <Check className="h-3 w-3 shrink-0" />
          : recommendation === 'refine' ? <RotateCw className="h-3 w-3 shrink-0" />
          : <Sparkles className="h-3 w-3 shrink-0" />}
        <span>{banner.label}</span>
      </div>

      <div className="p-4 space-y-3">
        {(analysis.sections.looksGood.length > 0 || analysis.sections.worthFlagging.length > 0) && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {analysis.sections.looksGood.length > 0 && (
              <Section title="Looks good" icon={Check} iconClass="text-success">
                {analysis.sections.looksGood.map((b, i) => <li key={i}>{b.text}</li>)}
              </Section>
            )}
            {analysis.sections.worthFlagging.length > 0 && (
              <Section title="Worth flagging" icon={AlertTriangle} iconClass="text-warning">
                {analysis.sections.worthFlagging.map((b, i) => <li key={i}>{b.text}</li>)}
              </Section>
            )}
          </div>
        )}
        {analysis.sections.decisions.length > 0 && (
          <DecisionsSection
            decisions={analysis.sections.decisions}
            recorded={recordedDecisions}
            onRecord={onRecordDecision}
          />
        )}
      </div>

      {(onProceed || onIterate) && (
        <div className="flex items-center justify-between gap-2 px-4 py-3 border-t border-border bg-card">
          <div className="text-[11px] text-muted-foreground">
            {nextStepLabel ? <>next: <span className="font-medium text-foreground">{nextStepLabel}</span></> : 'workflow complete'}
          </div>
          <div className="flex items-center gap-1.5">
            {onIterate && (
              <Button
                variant={recommendation === 'refine' ? 'default' : 'outline'}
                size="sm"
                onClick={onIterate}
                className={cn('h-7 text-xs gap-1', recommendation === 'refine' && 'shadow-sm')}
              >
                <RotateCw className="h-3 w-3" />
                Iterate step
              </Button>
            )}
            {onProceed && (
              <Button
                size="sm"
                onClick={onProceed}
                variant={recommendation === 'proceed' ? 'default' : 'outline'}
                className={cn(
                  'h-7 text-xs gap-1',
                  recommendation === 'proceed' && 'bg-success text-success-foreground hover:bg-success/90 shadow-sm',
                )}
              >
                {nextStepLabel ? 'Proceed' : 'Finish'}
                <ArrowRight className="h-3 w-3" />
              </Button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function Section({ title, icon: Icon, iconClass, children }: { title: string; icon: typeof Check; iconClass: string; children: React.ReactNode }) {
  return (
    <div className="min-w-0">
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1 flex items-center gap-1.5">
        <Icon className={`h-2.5 w-2.5 ${iconClass}`} />
        {title}
      </div>
      <ul className="text-[12.5px] leading-snug space-y-0.5 pl-3.5 list-disc marker:text-muted-foreground">{children}</ul>
    </div>
  );
}

interface DecisionsSectionProps {
  decisions: AnalysisDecision[];
  recorded: RecordedDecision[];
  onRecord?: (decision: RecordedDecision) => void;
}

function DecisionsSection({ decisions, recorded, onRecord }: DecisionsSectionProps) {
  return (
    <div>
      <div className="text-[11px] uppercase tracking-wider text-muted-foreground mb-1.5 flex items-center gap-1.5">
        <HelpCircle className="h-3 w-3 text-info" />
        Decisions for you
      </div>
      <div className="space-y-2.5">
        {decisions.map((d, i) => {
          const recordedChoice = recorded.find(r => r.question === d.text)?.chosen;
          return (
            <DecisionItem
              key={i}
              decision={d}
              recordedChoice={recordedChoice}
              onChoose={onRecord ? (chosen) => onRecord({ question: d.text, chosen }) : undefined}
            />
          );
        })}
      </div>
    </div>
  );
}

interface DecisionItemProps {
  decision: AnalysisDecision;
  recordedChoice?: string;
  onChoose?: (chosen: string) => void;
}

function DecisionItem({ decision, recordedChoice, onChoose }: DecisionItemProps) {
  const [otherOpen, setOtherOpen] = useState(false);
  const [otherText, setOtherText] = useState('');
  const options = decision.options ?? [];
  const locked = !!recordedChoice;

  const handleChoose = (label: string) => {
    if (locked || !onChoose) return;
    onChoose(label);
    setOtherOpen(false);
    setOtherText('');
  };

  const submitOther = () => {
    const trimmed = otherText.trim();
    if (!trimmed || locked || !onChoose) return;
    onChoose(trimmed);
    setOtherOpen(false);
    setOtherText('');
  };

  return (
    <div className={cn('rounded-md border border-info/15 bg-card p-2.5 space-y-2', locked && 'opacity-90')}>
      <p className="text-sm text-foreground">{decision.text}</p>

      {locked ? (
        <div className="flex items-center gap-1.5 text-xs">
          <Check className="h-3 w-3 text-success" />
          <span className="text-muted-foreground">you chose:</span>
          <span className="font-medium">{recordedChoice}</span>
        </div>
      ) : (
        <>
          <div className="flex flex-wrap gap-1.5">
            {options.map((opt, i) => (
              <button
                key={i}
                type="button"
                onClick={() => handleChoose(opt)}
                disabled={!onChoose}
                className={cn(
                  'inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-[11px] transition-colors',
                  opt === decision.recommendedOption
                    ? 'border-primary/40 bg-primary/10 text-primary hover:bg-primary/15'
                    : 'border-border bg-card hover:border-primary/40',
                  !onChoose && 'cursor-not-allowed opacity-60',
                )}
              >
                {opt}
                {opt === decision.recommendedOption && (
                  <span className="text-[9px] uppercase tracking-wider opacity-70">rec'd</span>
                )}
              </button>
            ))}
            {onChoose && (
              <button
                type="button"
                onClick={() => setOtherOpen(v => !v)}
                className="inline-flex items-center gap-1 rounded-full border border-dashed border-border px-2.5 py-1 text-[11px] text-muted-foreground hover:border-primary/40 hover:text-foreground transition-colors"
              >
                <MessageSquare className="h-2.5 w-2.5" />
                Other
              </button>
            )}
          </div>
          {otherOpen && (
            <div className="space-y-1.5">
              <textarea
                value={otherText}
                onChange={(e) => setOtherText(e.target.value)}
                onKeyDown={(e) => {
                  if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
                    e.preventDefault();
                    submitOther();
                  } else if (e.key === 'Escape') {
                    setOtherOpen(false);
                    setOtherText('');
                  }
                }}
                placeholder="Type your own answer…"
                aria-label="Custom decision answer"
                rows={2}
                className="w-full bg-muted/30 border border-border rounded-md px-2.5 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-primary/30 resize-none"
                autoFocus
              />
              <div className="flex justify-end gap-1.5">
                <Button variant="ghost" size="sm" onClick={() => { setOtherOpen(false); setOtherText(''); }} className="h-6 text-[11px]">
                  Cancel
                </Button>
                <Button size="sm" onClick={submitOther} disabled={!otherText.trim()} className="h-6 text-[11px]">
                  Record
                </Button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
